import { wrapTool, toolError, toolSuccess } from '../utils/ToolUtils';
import type { AnyToolFunction } from '../types';
import { logger } from '@/utils/logger';
import type { CanvasContentType, CanvasData, CanvasPushPayload } from '@/types/AgentCanvas';
import { secureRandomHex } from '@/utils/crypto-random';
import { importWithRetry } from '@/utils/dynamicImport';

/**
 * ISSUE-035 Fix: Z-Index Ceiling for Canvas Shapes
 * Prevents agents from rendering shapes with excessively high z-index
 * that could obscure interactive UI elements and lock users out.
 */
const MAX_Z_INDEX = 1000;

/**
 * CanvasTools — Agent-to-UI Push (A2UI)
 *
 * Allows agents to push structured visual content (charts, tables, cards,
 * markdown) directly into the user's workspace. The user sees a live panel
 * with the pushed content — no more text-only responses for dashboards.
 */
export const CanvasTools = {
    /**
     * Push structured visual content to the user's workspace canvas.
     *
     * Supported types:
     * - "chart": Recharts-powered data visualization (bar, line, pie, area, scatter, radar).
     *   data: { chartType, data: [{...}], xKey, yKeys, colors? }
     * - "table": Sortable data table.
     *   data: { columns: [{ key, label, align? }], rows: [{...}] }
     * - "card": Dashboard-style info cards.
     *   data: { cards: [{ title, value, subtitle?, icon?, trend?, trendValue? }] }
     * - "markdown": Rich formatted text.
     *   data: { content: "# Heading\nBody text..." }
     */
    canvas_push: wrapTool('canvas_push', async (args: {
        type: CanvasContentType;
        title: string;
        data: CanvasData;
        agentId?: string;
    }) => {
        try {
            const { type, title, data, agentId = 'generalist' } = args;

            const validTypes: CanvasContentType[] = ['chart', 'table', 'card', 'html', 'markdown'];
            if (!validTypes.includes(type)) {
                return toolError(
                    `Invalid canvas type "${type}". Must be one of: ${validTypes.join(', ')}`,
                    'CANVAS_INVALID_TYPE'
                );
            }

            if (!title || title.trim().length === 0) {
                return toolError('Canvas title is required', 'CANVAS_MISSING_TITLE');
            }

            const payload: CanvasPushPayload = {
                id: secureRandomHex(8),
                type,
                title: title.trim(),
                data,
                agentId,
                createdAt: Date.now(),
            };

            // Push to the store — dynamically import to avoid circular deps
            const { useStore } = await importWithRetry(() => import('@/core/store'));
            const store = useStore.getState();
            store.pushCanvas(payload);

            // Phase 2: If valid project context exists, also create a persistent agent_output block
            const currentProjectId = store.currentProjectId;
            if (currentProjectId && typeof store.addCanvasBlock === 'function') {
                const now = Date.now();
                const rawData = data as Record<string, unknown>;
                const excerpt = typeof rawData.content === 'string'
                    ? rawData.content.slice(0, 160)
                    : Array.isArray(rawData.cards) && rawData.cards[0]
                    ? `${(rawData.cards[0] as { title?: string }).title || ''}: ${(rawData.cards[0] as { value?: string }).value || ''}`
                    : title.trim();

                store.addCanvasBlock({
                    type: 'agent_output',
                    position: { x: 350, y: 200 },
                    size: { width: 340, height: 260 },
                    zIndex: 2,
                    snapshot: {
                        title: title.trim(),
                        excerpt,
                        cachedAt: now,
                    },
                    settings: {
                        presentation: type,
                        title: title.trim(),
                        agentData: rawData,
                    },
                    provenance: {
                        creatorType: 'agent',
                        creatorId: agentId,
                        agentName: agentId,
                        operation: 'canvas_push',
                        timestamp: now,
                        correlationId: payload.id,
                    },
                });
            }

            logger.info(`[CanvasTools] Pushed "${title}" (${type}) to canvas`);
            return toolSuccess(
                { panelId: payload.id, type, title: payload.title },
                `Canvas panel "${payload.title}" pushed successfully — the user can now see it.`
            );
        } catch (error: unknown) {
            logger.error('[CanvasTools] canvas_push error:', error);
            return toolError(`Failed to push canvas: ${String(error)}`, 'CANVAS_PUSH_ERROR');
        }
    }),

    /**
     * Clear all agent-pushed canvas panels.
     */
    canvas_clear: wrapTool('canvas_clear', async () => {
        try {
            const { useStore } = await importWithRetry(() => import('@/core/store'));
            useStore.getState().clearCanvas();
            logger.info('[CanvasTools] Canvas cleared');
            return toolSuccess(null, 'Canvas cleared successfully.');
        } catch (error: unknown) {
            logger.error('[CanvasTools] canvas_clear error:', error);
            return toolError(`Failed to clear canvas: ${String(error)}`, 'CANVAS_CLEAR_ERROR');
        }
    }),

    /**
     * Inspect active agent canvas panels (Agent-to-UI / A2UI).
     * Returns whether the canvas drawer is open, total panel count,
     * and a structured list of active panels with concise summaries.
     * Optionally takes a specific panelId to fetch full data.
     */
    canvas_inspect: wrapTool('canvas_inspect', async (args?: {
        panelId?: string;
        includeData?: boolean;
    }) => {
        try {
            const { useStore } = await importWithRetry(() => import('@/core/store'));
            const store = useStore.getState();
            const canvasPanels = store.canvasPanels ?? [];
            const isCanvasOpen = Boolean(store.isCanvasOpen);
            const currentDoc = store.currentDoc ?? null;

            if (args?.panelId) {
                const target = canvasPanels.find((p) => p.id === args.panelId);
                if (!target) {
                    return toolError(`Canvas panel with ID "${args.panelId}" not found.`, 'CANVAS_PANEL_NOT_FOUND');
                }
                return toolSuccess(
                    {
                        panel: target,
                        isCanvasOpen,
                    },
                    `Retrieved canvas panel "${target.title}" (${target.type}).`
                );
            }

            const panelsSummary = canvasPanels.map((p) => {
                let excerpt = '';
                const rawData = p.data as Record<string, unknown> | undefined;
                if (p.type === 'markdown' || p.type === 'html') {
                    excerpt = typeof rawData?.content === 'string' ? rawData.content.slice(0, 200) : '';
                } else if (p.type === 'card' && Array.isArray(rawData?.cards)) {
                    excerpt = (rawData.cards as Array<{ title?: string; value?: string | number }>)
                        .slice(0, 3)
                        .map((c) => `${c.title || 'Card'}: ${c.value ?? ''}`)
                        .join(', ');
                } else if (p.type === 'table' && Array.isArray(rawData?.rows)) {
                    excerpt = `${rawData.rows.length} rows, columns: ${(rawData.columns as Array<{ label?: string }> || []).map((c) => c.label).join(', ')}`;
                } else if (p.type === 'chart' && Array.isArray(rawData?.data)) {
                    excerpt = `${rawData.chartType || 'chart'} with ${rawData.data.length} data points`;
                }

                return {
                    id: p.id,
                    type: p.type,
                    title: p.title,
                    agentId: p.agentId,
                    createdAt: p.createdAt,
                    excerpt,
                    ...(args?.includeData ? { data: p.data } : {}),
                };
            });

            const activeEditor = currentDoc
                ? {
                    id: currentDoc.id,
                    width: currentDoc.width,
                    height: currentDoc.height,
                    layerCount: currentDoc.layers?.length ?? 0,
                    layers: (currentDoc.layers || []).map((l) => ({ id: l.id, kind: l.kind, name: l.name, visible: l.visible })),
                }
                : null;

            const summaryMessage = canvasPanels.length === 0
                ? (activeEditor
                    ? `Agent canvas drawer has 0 pushed panels, but layer editor is active on doc "${activeEditor.id}" (${activeEditor.layerCount} layers).`
                    : 'Agent canvas drawer is currently empty (0 panels).')
                : `Agent canvas has ${canvasPanels.length} active panel(s)${isCanvasOpen ? ' (drawer currently open)' : ' (drawer closed)'}.`;

            return toolSuccess(
                {
                    isCanvasOpen,
                    panelCount: canvasPanels.length,
                    panels: panelsSummary,
                    activeEditor,
                },
                summaryMessage
            );
        } catch (error: unknown) {
            logger.error('[CanvasTools] canvas_inspect error:', error);
            return toolError(`Failed to inspect canvas: ${String(error)}`, 'CANVAS_INSPECT_ERROR');
        }
    }),

    /**
     * Draw a shape on the canvas with validated z-index (ISSUE-035 Fix).
     * Prevents agents from rendering shapes with excessively high z-index
     * that could obscure interactive UI elements.
     */
    draw_shape: wrapTool('draw_shape', async (args: {
        shapeType: 'rect' | 'circle' | 'line' | 'text' | 'image';
        x: number;
        y: number;
        width?: number;
        height?: number;
        radius?: number;
        color?: string;
        fill?: boolean;
        stroke?: string;
        zIndex?: number;
        label?: string;
        imageUrl?: string;
    }) => {
        try {
            const { shapeType, x, y, width, height, radius, color = '#000', fill = true, stroke, label, imageUrl, zIndex = 0 } = args;

            // ISSUE-035: Validate and clamp z-index to prevent UI sabotage
            if (zIndex < 0) {
                return toolError(
                    `Invalid z-index: ${zIndex}. Z-index must be >= 0.`,
                    'CANVAS_INVALID_Z_INDEX'
                );
            }

            if (zIndex > MAX_Z_INDEX) {
                return toolError(
                    `Z-index ${zIndex} exceeds maximum allowed value of ${MAX_Z_INDEX}. ` +
                    `Shapes are constrained to prevent obscuring UI elements. ` +
                    `Please use z-index <= ${MAX_Z_INDEX}.`,
                    'CANVAS_Z_INDEX_CEILING'
                );
            }

            const validShapes = ['rect', 'circle', 'line', 'text', 'image'];
            if (!validShapes.includes(shapeType)) {
                return toolError(
                    `Invalid shape type "${shapeType}". Must be one of: ${validShapes.join(', ')}`,
                    'CANVAS_INVALID_SHAPE'
                );
            }

            // Validate dimensions based on shape type
            if (shapeType === 'rect' && (!width || !height)) {
                return toolError('Rectangle requires width and height', 'CANVAS_MISSING_DIMS');
            }
            if (shapeType === 'circle' && !radius) {
                return toolError('Circle requires radius', 'CANVAS_MISSING_DIMS');
            }
            if (shapeType === 'text' && !label) {
                return toolError('Text shape requires a label', 'CANVAS_MISSING_DIMS');
            }
            if (shapeType === 'line' && !width && !height) {
                return toolError('Line shape requires width or height to define its extent', 'CANVAS_MISSING_DIMS');
            }
            if (shapeType === 'image' && !imageUrl) {
                return toolError('Image shape requires an imageUrl', 'CANVAS_MISSING_DIMS');
            }

            // Fix ISSUE-053: Directly render to fabric.js canvas via canvasOps
            const { canvasOps } = await importWithRetry(() => import('@/modules/creative/services/CanvasOperationsService'));
            const fabric = await importWithRetry(() => import('fabric'));
            const canvas = canvasOps.getCanvas();

            if (!canvas) {
                return toolError('Creative canvas is not active. Shape was not rendered.', 'CANVAS_NOT_ACTIVE');
            }

            // Fabric.js canvas is active, draw directly
            const commonProps = { left: x, top: y, fill: fill ? color : 'transparent', stroke: stroke || (fill ? undefined : color), data: { zIndex } };

            if (shapeType === 'image') {
                try {
                    const img = await fabric.Image.fromURL(imageUrl!, { crossOrigin: 'anonymous' });
                    if (!img) {
                        return toolError(`Failed to load image from URL: ${imageUrl}`);
                    }
                    img.set({ ...commonProps });
                    if (width) img.scaleToWidth(width);
                    else if (height) img.scaleToHeight(height);
                    
                    canvas.add(img);
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    canvas.getObjects().sort((a: any, b: any) => {
                        const zA = (a.data?.zIndex as number) || 0;
                        const zB = (b.data?.zIndex as number) || 0;
                        return zA - zB;
                    });
                    canvas.renderAll();
                    
                    logger.info(`[CanvasTools] Drew image shape at (${x}, ${y}) with z-index ${zIndex}`);
                    return toolSuccess(
                        { shapeType, zIndex },
                        `Image drawn successfully at (${x}, ${y}) with z-index ${zIndex} directly onto the creative canvas.`
                    );
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } catch (err: any) {
                    return toolError(`Failed to load image from URL: ${imageUrl}. Error: ${err.message}`);
                }
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let fabricObj: any = null;

            if (shapeType === 'rect') {
                fabricObj = new fabric.Rect({ ...commonProps, width, height });
            } else if (shapeType === 'circle') {
                fabricObj = new fabric.Circle({ ...commonProps, radius: radius! });
            } else if (shapeType === 'line') {
                fabricObj = new fabric.Line([x, y, x + (width || 0), y + (height || 0)], { ...commonProps, fill: undefined, stroke: color });
            } else if (shapeType === 'text') {
                fabricObj = new fabric.Text(label!, { ...commonProps, fontSize: 24, fill: color });
            }

            if (fabricObj) {
                canvas.add(fabricObj);
                // Simple zIndex sorting (Fabric doesn't natively auto-sort by a custom zIndex property without manual reordering)
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                canvas.getObjects().sort((a: any, b: any) => {
                    const zA = (a.data?.zIndex as number) || 0;
                    const zB = (b.data?.zIndex as number) || 0;
                    return zA - zB;
                });
                canvas.renderAll();
            }

            logger.info(`[CanvasTools] Drew ${shapeType} shape at (${x}, ${y}) with z-index ${zIndex}`);
            return toolSuccess(
                { shapeType, zIndex },
                `Shape "${shapeType}" drawn successfully at (${x}, ${y}) with z-index ${zIndex} directly onto the creative canvas.`
            );
        } catch (error: unknown) {
            logger.error('[CanvasTools] draw_shape error:', error);
            return toolError(`Failed to draw shape: ${String(error)}`, 'CANVAS_DRAW_ERROR');
        }
    }),
    /**
     * Open a gallery image into the non-destructive canvas layer editor
     * (Workstream C2). Returns the docId.
     */
    canvas_open_image: wrapTool('canvas_open_image', async (args: { imageIndex: number }) => {
        const { useStore } = await importWithRetry(() => import('@/core/store'));
        const store = useStore.getState();
        const { generatedHistory, uploadedImages, currentProjectId } = store;
        const target = generatedHistory?.[args.imageIndex] ?? uploadedImages?.[args.imageIndex];
        if (!target?.url) return toolError('imageIndex did not resolve to an image.', 'INVALID_INPUT');
        try {
            store.openImage(target.url, currentProjectId ?? 'project_default');
            const docId = store.currentDoc?.id ?? null;
            return toolSuccess({ docId }, `Opened image ${args.imageIndex} into the layer editor (${docId}).`);
        } catch (error) {
            logger.error('[CanvasTools] canvas_open_image error:', error);
            return toolError(error instanceof Error ? error.message : String(error), 'CANVAS_OPEN_ERROR');
        }
    }),

    /**
     * Add a raster layer from a gallery/upload index to the open doc.
     */
    canvas_add_layer: wrapTool('canvas_add_layer', async (args: { docId: string; imageIndex: number }) => {
        const { useStore } = await importWithRetry(() => import('@/core/store'));
        const store = useStore.getState();
        if (!store.currentDoc || store.currentDoc.id !== args.docId) {
            return toolError(`No open doc "${args.docId}". Open an image first.`, 'INVALID_INPUT');
        }
        const target = store.generatedHistory?.[args.imageIndex] ?? store.uploadedImages?.[args.imageIndex];
        if (!target?.url) return toolError('imageIndex did not resolve to an image.', 'INVALID_INPUT');
        const layerId = store.addRasterLayer(target.url);
        return toolSuccess({ layerId }, `Added raster layer "${layerId}" to doc ${args.docId}.`);
    }),

    /**
     * Merge an adjustment patch over the neutral stack for a raster layer.
     */
    canvas_set_adjustments: wrapTool('canvas_set_adjustments', async (args: { docId: string; layerId: string; adjustments: Record<string, number> }) => {
        const { useStore } = await importWithRetry(() => import('@/core/store'));
        const store = useStore.getState();
        if (!store.currentDoc || store.currentDoc.id !== args.docId) {
            return toolError(`No open doc "${args.docId}".`, 'INVALID_INPUT');
        }
        try {
            store.setAdjustments(args.layerId, args.adjustments);
            return toolSuccess({ layerId: args.layerId, adjustments: args.adjustments }, `Set adjustments on layer ${args.layerId}.`);
        } catch (error) {
            return toolError(error instanceof Error ? error.message : String(error), 'CANVAS_ADJUST_ERROR');
        }
    }),

    /**
     * Export the open doc as a raster asset → history item + result URL.
     */
    canvas_export: wrapTool('canvas_export', async (args: { docId?: string; format?: 'png' | 'jpeg'; scale?: number }) => {
        const { useStore } = await importWithRetry(() => import('@/core/store'));
        const store = useStore.getState();
        if (!store.currentDoc) return toolError('No open doc to export. Open an image first.', 'INVALID_INPUT');
        const doc = store.currentDoc;
        if (args.docId && args.docId !== doc.id) return toolError(`Doc "${args.docId}" is not open.`, 'INVALID_INPUT');
        try {
            // Deterministic export: composite the layers' raster source over the
            // background at doc resolution. The Fabric canvas applies per-layer
            // adjustment filters; here we emit the doc contract URL as a PNG
            // data URL for the tool result (the interactive Editor performs the
            // full filter bake). Format honored for the history record.
            const scale = args.scale ?? 1;
            const width = Math.round(doc.width * scale);
            const height = Math.round(doc.height * scale);
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) throw new Error('No 2D context');
            ctx.fillStyle = doc.background || '#000000';
            ctx.fillRect(0, 0, width, height);
            for (const layer of doc.layers) {
                if (!layer.visible) continue;
                if (layer.kind === 'text') {
                    try {
                        const { rasterizeTextLayer } = await import('@/services/canvas/textLayerRaster');
                        const { dataUrl } = await rasterizeTextLayer(layer, scale);
                        const img = new Image();
                        img.src = dataUrl;
                        await img.decode().catch(() => {});
                        if (img.width > 0) {
                            ctx.save();
                            ctx.globalAlpha = layer.opacity ?? 1;
                            ctx.drawImage(img, (layer.x ?? 0) * scale, (layer.y ?? 0) * scale);
                            ctx.restore();
                        }
                    } catch (textErr) {
                        logger.warn('[CanvasTools] Failed to rasterize text layer during export:', textErr);
                    }
                    continue;
                }

                if (layer.kind === 'raster') {
                    const img = new Image();
                    img.src = layer.src;
                    await img.decode().catch(() => {});
                    if (img.width > 0) {
                        ctx.save();
                        ctx.globalAlpha = layer.opacity ?? 1;
                        const dw = img.width * (layer.scaleX ?? 1) * scale;
                        const dh = img.height * (layer.scaleY ?? 1) * scale;
                        ctx.drawImage(img, (layer.x ?? 0) * scale, (layer.y ?? 0) * scale, dw, dh);
                        ctx.restore();
                    }
                }
            }
            const mime = args.format === 'jpeg' ? 'image/jpeg' : 'image/png';
            const url = canvas.toDataURL(mime);
            const historyId = `canvas_export_${Date.now()}`;
            store.addToHistory?.({
                id: historyId, url, prompt: 'Layer editor export', type: 'image',
                timestamp: Date.now(), projectId: doc.projectId,
                meta: JSON.stringify({ source: 'canvas_export', docId: doc.id }),
                tags: ['canvas_export'], origin: 'canvas-export'
            });

            try {
                const { AssetVersionService } = await importWithRetry(() => import('@/services/assets/AssetVersionService'));
                await AssetVersionService.recordVersion({
                    assetId: historyId,
                    parentVersionId: null,
                    url,
                    source: 'canvas-export',
                    provenance: { provider: 'indii', note: `Canvas doc export ${doc.id} (${width}x${height})` },
                    tags: ['canvas_export', `doc:${doc.id}`]
                });
            } catch (verErr) {
                logger.warn('[CanvasTools] AssetVersionService recordVersion failed for canvas_export:', verErr);
            }

            return toolSuccess({ url, width, height }, `Exported layer doc ${doc.id} as ${args.format ?? 'png'} (${width}×${height}).`);
        } catch (error) {
            logger.error('[CanvasTools] canvas_export error:', error);
            return toolError(error instanceof Error ? error.message : String(error), 'CANVAS_EXPORT_ERROR');
        }
    }),

    /**
     * Headless typography overlay — burns custom sayings, lyrics, or titles onto an
     * image without requiring an active DOM Fabric.js canvas. Perfect for remote
     * Boardroom commands requesting text overlays.
     */
    composite_image_typography: wrapTool('composite_image_typography', async (args: {
        imageUrl: string;
        text: string;
        position?: 'top' | 'center' | 'bottom';
        fontFamily?: string;
        fontSize?: number;
        color?: string;
        badgeStyle?: 'shadow' | 'pill' | 'banner' | 'none';
    }) => {
        try {
            const { imageUrl, text, position = 'bottom', fontFamily = 'Impact, sans-serif', fontSize, color = '#ffffff', badgeStyle = 'shadow' } = args;
            if (!imageUrl) return toolError('imageUrl is required', 'MISSING_IMAGE_URL');
            if (!text || !text.trim()) return toolError('text is required', 'MISSING_TEXT');

            const { safeStorageFetch } = await importWithRetry(() => import('@/services/storage/safeStorageFetch'));
            const { blob } = await safeStorageFetch(imageUrl);
            const objectUrl = URL.createObjectURL(blob);

            const img = new Image();
            img.src = objectUrl;
            await img.decode().catch(() => {});
            URL.revokeObjectURL(objectUrl);

            if (img.width === 0 || img.height === 0) {
                return toolError('Could not decode master image dimensions', 'IMAGE_DECODE_FAILED');
            }

            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            if (!ctx) return toolError('Could not allocate 2D canvas context', 'CANVAS_CONTEXT_FAILED');

            // Draw base image
            ctx.drawImage(img, 0, 0);

            // Compute font size relative to image resolution (default ~6% of canvas height, min 28px)
            const resolvedFontSize = fontSize || Math.max(28, Math.round(canvas.height * 0.055));
            ctx.font = `bold ${resolvedFontSize}px ${fontFamily}`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            // Split into wrapped lines fitting within 85% of canvas width
            const maxWidth = canvas.width * 0.85;
            const words = text.trim().split(/\s+/);
            const lines: string[] = [];
            let currentLine = '';

            for (const word of words) {
                const testLine = currentLine ? `${currentLine} ${word}` : word;
                const metrics = ctx.measureText(testLine);
                if (metrics.width > maxWidth && currentLine) {
                    lines.push(currentLine);
                    currentLine = word;
                } else {
                    currentLine = testLine;
                }
            }
            if (currentLine) lines.push(currentLine);

            const lineHeight = resolvedFontSize * 1.25;
            const totalTextHeight = lines.length * lineHeight;

            // Compute Y position
            let startY = canvas.height * 0.85 - (totalTextHeight / 2);
            if (position === 'top') {
                startY = canvas.height * 0.15 + (totalTextHeight / 2);
            } else if (position === 'center') {
                startY = (canvas.height / 2) - (totalTextHeight / 2);
            }

            // Draw optional pill / banner backing
            if (badgeStyle === 'pill' || badgeStyle === 'banner') {
                ctx.save();
                ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
                const padX = resolvedFontSize * 0.8;
                const padY = resolvedFontSize * 0.4;
                const maxLineWidth = Math.max(...lines.map(l => ctx.measureText(l).width));
                const boxW = badgeStyle === 'banner' ? canvas.width : Math.min(canvas.width * 0.95, maxLineWidth + (padX * 2));
                const boxH = totalTextHeight + (padY * 2);
                const boxX = badgeStyle === 'banner' ? 0 : (canvas.width - boxW) / 2;
                const boxY = startY - (resolvedFontSize / 2) - padY;

                if (badgeStyle === 'pill') {
                    const radius = Math.min(24, resolvedFontSize * 0.5);
                    ctx.beginPath();
                    ctx.roundRect(boxX, boxY, boxW, boxH, radius);
                    ctx.fill();
                } else {
                    ctx.fillRect(boxX, boxY, boxW, boxH);
                }
                ctx.restore();
            }

            // Draw text with shadow/stroke
            lines.forEach((line, idx) => {
                const y = startY + (idx * lineHeight);
                ctx.save();
                if (badgeStyle === 'shadow') {
                    ctx.shadowColor = 'rgba(0, 0, 0, 0.9)';
                    ctx.shadowBlur = Math.max(8, resolvedFontSize * 0.2);
                    ctx.shadowOffsetX = 0;
                    ctx.shadowOffsetY = Math.max(3, resolvedFontSize * 0.08);

                    // Also stroke text for crisp separation
                    ctx.lineWidth = Math.max(2, resolvedFontSize * 0.08);
                    ctx.strokeStyle = '#000000';
                    ctx.strokeText(line, canvas.width / 2, y);
                }
                ctx.fillStyle = color;
                ctx.fillText(line, canvas.width / 2, y);
                ctx.restore();
            });

            // Export to data URL and save to store/history
            const outputUrl = canvas.toDataURL('image/jpeg', 0.92);
            const { useStore } = await importWithRetry(() => import('@/core/store'));
            const store = useStore.getState();
            const currentUploads = store.uploadedImages || [];
            const typographyItem: import('@/core/types/history').HistoryItem = {
                id: `typography_${Date.now()}`,
                url: outputUrl,
                prompt: `Typography: "${text}"`,
                type: 'image',
                timestamp: Date.now(),
                projectId: store.currentProjectId || 'default',
                tags: ['typography_composite', 'saying_overlay'],
                origin: 'canvas-export',
            };
            useStore.setState({ uploadedImages: [...currentUploads, typographyItem] });
            store.addToHistory?.(typographyItem);

            return toolSuccess(
                { outputUrl, width: canvas.width, height: canvas.height, text, linesCount: lines.length },
                `Successfully burned saying onto image (${canvas.width}×${canvas.height}).`
            );
        } catch (error) {
            logger.error('[CanvasTools] composite_image_typography error:', error);
            return toolError(error instanceof Error ? error.message : String(error), 'COMPOSITE_TYPOGRAPHY_ERROR');
        }
    }),

    /**
     * Batch social derivatives exporter — crops or fits master image into
     * standard deliverable aspect ratios (9:16 Stories/TikTok, 1:1 Feed, 4:5 Portrait, 16:9 Landscape)
     * using SmartCrop and offscreen canvas.
     */
    export_social_derivatives: wrapTool('export_social_derivatives', async (args: {
        imageUrl: string;
        platformIds?: string[];
        quality?: number;
    }) => {
        try {
            const { imageUrl, platformIds = ['instagram_story', 'instagram_square', 'instagram_portrait', 'youtube_thumbnail'], quality = 0.9 } = args;
            if (!imageUrl) return toolError('imageUrl is required', 'MISSING_IMAGE_URL');

            const { exportMasterAsset } = await importWithRetry(() => import('@/services/export/AssetExporter'));
            const presets = platformIds.map(dimensionId => ({
                dimensionId,
                fit: 'cover' as const,
            }));

            const results = await exportMasterAsset({
                masterUrl: imageUrl,
                presets,
                quality,
                format: 'image/jpeg',
            });

            const { useStore } = await importWithRetry(() => import('@/core/store'));
            const store = useStore.getState();
            const currentUploads = store.uploadedImages || [];
            const newHistoryItems: import('@/core/types/history').HistoryItem[] = results.map((r, idx) => ({
                id: `derivative_${r.platformId}_${Date.now()}_${idx}`,
                url: r.url,
                prompt: `Social derivative (${r.platformId})`,
                type: 'image',
                timestamp: Date.now(),
                projectId: store.currentProjectId || 'default',
                origin: 'canvas-export',
                tags: ['social_derivative', r.platformId],
            }));
            useStore.setState({ uploadedImages: [...currentUploads, ...newHistoryItems] });

            return toolSuccess(
                {
                    count: results.length,
                    derivatives: results.map(r => ({
                        platformId: r.platformId,
                        width: r.width,
                        height: r.height,
                        url: r.url,
                    })),
                },
                `Successfully exported ${results.length} social derivatives (Story 9:16, Square 1:1, Portrait 4:5, Landscape 16:9).`
            );
        } catch (error) {
            logger.error('[CanvasTools] export_social_derivatives error:', error);
            return toolError(error instanceof Error ? error.message : String(error), 'EXPORT_SOCIAL_DERIVATIVES_ERROR');
        }
    })
} satisfies Record<string, AnyToolFunction>;
